import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Coins, Send, X } from "lucide-react";
import { requestCredits } from "@/lib/api";

/**
 * Modal shown when a user runs out of credits and wants to ask the admin for
 * more. The admin decides in the panel how many to actually grant.
 */
export default function CreditRequestModal({ onClose, onSubmitted }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState(10);
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await requestCredits(Math.max(1, Math.min(1000, parseInt(amount, 10) || 10)), message.trim());
      toast.success(t("credits.toast_sent"));
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(
        t("credits.toast_error", { msg: e.response?.data?.detail || e.message })
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="credit-request-modal"
    >
      <div
        className="max-w-md w-full rounded-2xl border border-white/10 bg-[color:var(--bg-elev)] p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-md">
            <Coins size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white leading-none">
              {t("credits.request_modal_title")}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="ml-auto text-[color:var(--fg-muted)] hover:text-white"
            data-testid="credit-request-close"
            aria-label="close"
          >
            <X size={16} />
          </button>
        </div>
        <p className="text-xs text-[color:var(--fg-secondary)] mb-5 leading-relaxed">
          {t("credits.request_modal_body")}
        </p>

        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("credits.request_amount")}
        </label>
        <input
          type="number"
          min={1}
          max={1000}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="w-full mb-4 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="credit-request-amount"
          autoFocus
        />

        <label className="block text-xs text-[color:var(--fg-muted)] mb-1">
          {t("credits.request_message")}
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          placeholder={t("credits.request_message_placeholder")}
          className="w-full mb-5 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm resize-none"
          data-testid="credit-request-message"
        />

        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-lg text-xs text-[color:var(--fg-secondary)] hover:text-white"
            data-testid="credit-request-cancel"
          >
            {t("credits.request_cancel")}
          </button>
          <button
            onClick={submit}
            disabled={submitting}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/30 disabled:opacity-50"
            data-testid="credit-request-submit"
          >
            <Send size={12} /> {t("credits.request_submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
